import {
    mbApplyTransparency,
    mbShadow,
    mbTextStyles,
} from '@mightybyte/rnw.utils.style-utils';
import React, { useCallback } from 'react';
import {
    Dimensions,
    FlatList,
    ListRenderItemInfo,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { FONT_STYLES, textStyles } from '../../../../../constants/textStyles';
import { ShadowView } from '../../../../helperComponents/ShadowView';
import { Spacer } from '../../../../helperComponents/Spacer';
import { UIActivityIndicator } from '@mightybyte/rnw.components.activity-indicators';
import { AgeGroup, ListResultUserDataForClient } from '@sapphicsavvy/business';
import { capitalize } from 'lodash';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { useMBInfiniteFetch } from '@mightybyte/rnw.utils.util-hooks';
import LoadingContent from '../../../../helperComponents/LoadingContent';
import { Filter } from '../modals/FilterModal';
import { ShouldSetResponderWrapper } from '../../../../../utils/shared';
import FastImage from 'react-native-fast-image';
import { ForumActivitySvg } from '../../../../../resources/svgComponents/ForumActivitySvg';

const SCREEN_WIDTH = Dimensions.get('screen').width;
const CARD_WIDTH = 173;
const NUMBER_OF_COLUMNS = Math.floor(SCREEN_WIDTH / CARD_WIDTH);
const LIMIT = 20;

type SapphicsViewProps = {
    likedOnly?: boolean;
    filter?: Filter;
    query?: string;
    onViewUser?: (userId: string) => void;
};

function useInfiniteListOfUsers(
    options: Pick<SapphicsViewProps, 'likedOnly' | 'filter' | 'query'>,
) {
    return trpc.sapphics.listOfUsers.useInfiniteQuery(
        {
            liked: !!options.likedOnly,
            filter: options.filter,
            search: options.query ?? undefined,
            limit: LIMIT,
        },
        {
            getNextPageParam: (lastPage) => lastPage.nextCursor,
            cacheTime: 0,
        },
    );
}

function getEmptyMessage(
    options: Pick<SapphicsViewProps, 'likedOnly' | 'filter' | 'query'>,
) {
    if (options.filter || options.query) {
        return 'No Sapphic found with the applied search text or filter';
    } else if (options.likedOnly) {
        return "You haven't liked any sapphic yet!";
    }
    return 'No Sapphic Found!.';
}

const SapphicsView = ({
    likedOnly,
    filter,
    query,
    onViewUser,
}: SapphicsViewProps) => {

    const listOfUsersHook = useInfiniteListOfUsers({
        likedOnly,
        filter,
        query,
    });
    const { data, loadMore, isLoading, isFetching } = useMBInfiniteFetch(
        listOfUsersHook,
        { prefillWhileLoading: true },
    );

    const renderItem = useCallback(
        ({
            item,
        }: ListRenderItemInfo<ListResultUserDataForClient | string>) => {
            const desc =
                typeof item === 'string'
                    ? 'Loading'
                    : `${capitalize(item.identity)}, ${
                          item.ageGroup === AgeGroup.lateTeens
                              ? 'Late Teens'
                              : item.ageGroup
                      }`;
            const title = typeof item === 'string' ? 'Loading' : item.userName;
            const onPress =
                typeof item === 'string'
                    ? undefined
                    : () => onViewUser?.(item.id);
            const hasForumAccount =
                typeof item !== 'string' && !!item.websiteToolboxUserId;

            return (
                <ShouldSetResponderWrapper>
                    <ShadowView
                        style={[styles.sapphicCard]}
                        onPress={onPress}
                        disabled={typeof item === 'string'}
                    >
                        <View>
                            {typeof item === 'string' ? (
                                <LoadingContent style={styles.sapphicCardImage} />
                            ) : (
                                <FastImage
                                    style={styles.sapphicCardImage}
                                    source={mbGetMediumImage(item.avatarUrl, {
                                        asUri: true,
                                    })}
                                />
                            )}
                            {hasForumAccount && (
                                <View style={styles.forumBadge}>
                                    <ForumActivitySvg
                                        size={18}
                                    />
                                </View>
                            )}
                        </View>
                        <Text
                            style={styles.sapphicCardTitleText}
                            numberOfLines={1}
                        >
                            {title}
                        </Text>
                        <Text
                            style={styles.sapphicCardDescText}
                            numberOfLines={1}
                        >
                            {desc}
                        </Text>
                    </ShadowView>
                </ShouldSetResponderWrapper>
            );
        },
        [onViewUser],
    );

    const itemSeparator = useCallback(
        () => (
            <ShouldSetResponderWrapper>
                <Spacer height={12} />
            </ShouldSetResponderWrapper>
        ),
        [],
    );
    const keyExtractor = useCallback(
        (item: ListResultUserDataForClient | string) =>
            typeof item === 'string' ? item : item.id,
        [],
    );

    const ListFooterComponent = useCallback(() => {
        return (
            <View style={styles.footer}>
                <UIActivityIndicator size={30} color="#F35E2E" />
            </View>
        );
    }, []);

    const ListEmptyComponent = useCallback(() => {
        const message = getEmptyMessage({ likedOnly, filter, query });
        return (
            <View style={styles.empty}>
                <Text style={styles.emptyText}>{message}</Text>
            </View>
        );
    }, [filter, likedOnly, query]);

    return (
        <ShouldSetResponderWrapper style={styles.container}>
            <FlatList
                numColumns={NUMBER_OF_COLUMNS}
                data={data as (ListResultUserDataForClient | string)[]}
                renderItem={renderItem}
                contentContainerStyle={styles.contentContainer}
                ItemSeparatorComponent={itemSeparator}
                keyExtractor={keyExtractor}
                onEndReached={loadMore}
                onEndReachedThreshold={0.7}
                ListFooterComponent={
                    isFetching && !isLoading && data.length >= LIMIT
                        ? ListFooterComponent
                        : null
                }
                ListEmptyComponent={ListEmptyComponent}
            />
        </ShouldSetResponderWrapper>
    );
};

export { SapphicsView };

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    contentContainer: {
        alignSelf: 'center',
        paddingTop: 12,
        paddingBottom: 12,
        flexGrow: 1,
        width: (CARD_WIDTH + 6 * 2) * NUMBER_OF_COLUMNS,
    },
    sapphicCard: {
        padding: 13,
        marginHorizontal: 6,
    },
    sapphicCardRight: {
        marginRight: 12,
    },
    sapphicCardImage: {
        width: 147,
        height: 158,
        borderRadius: 8,
    },
    sapphicCardTitleText: mbTextStyles([
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            fontSize: 22,
            color: '#F35E2E',
            textAlign: 'center',
            marginTop: 12,
            width: 147,
            textTransform: 'uppercase',
        },
    ]),
    sapphicCardDescText: mbTextStyles([
        textStyles.smallText,
        {
            color: mbApplyTransparency('#000000', 0.55),
            marginTop: 4,
            width: 147,
        },
    ]),
    action: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: 17,
        top: 17,
        backgroundColor: '#FFFFFF',
        borderRadius: 360,
        zIndex: 12,
        ...mbShadow({
            offsetWidth: 4,
            offsetHeight: 0,
            color: '#000000',
            opacity: 0.25,
            radius: 10,
            elevation: 0,
        }),
    },
    forumBadge: {
        width: 28,
        height: 28,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        right: 8,
        top: 8,
        backgroundColor: '#FFFFFF',
        borderRadius: 360,
        zIndex: 10,
        ...mbShadow({
            offsetWidth: 2,
            offsetHeight: 0,
            color: '#000000',
            opacity: 0.2,
            radius: 6,
            elevation: 0,
        }),
    },
    empty: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyText: mbTextStyles([
        textStyles.smallText,
        {
            color: mbApplyTransparency('#000000', 0.55),
        },
    ]),
    footer: {
        height: 60,
    },
});
